import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpParams } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CardModule, GridModule, ButtonModule, SpinnerModule, BadgeModule, AlertModule } from '@coreui/angular-pro';
import { TranslatePipe, TranslationModule } from 'i18n';
import { Logger } from 'shared';

// WPCP admin board (spec v3.0): user search → summary, point awarding/correction,
// special release, manual WPGH membership booking. Gated to the super admin server-side.
@Component({
  selector: 'app-wpcp-admin-page',
  standalone: true,
  imports: [CommonModule, FormsModule, CardModule, GridModule, ButtonModule, SpinnerModule, BadgeModule, AlertModule, TranslationModule, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './wpcp-admin-page.component.html'
})
export class WpcpAdminPageComponent {
  private http = inject(HttpClient);
  private destroyRef = inject(DestroyRef);
  private logger = new Logger('WpcpAdminPageComponent');

  // search
  queryModel = '';
  results = signal<any[]>([]);
  searching = signal(false);

  // members that already have points, sorted by active points desc
  topAccounts = signal<any[]>([]);

  // selected member
  selected = signal<any | null>(null);
  summary = signal<any | null>(null);
  history = signal<any[]>([]);
  loading = signal(false);
  message = signal<{ type: string; text: string } | null>(null);

  // matrix + products for the forms
  categories = signal<any[]>([]);
  rules = signal<any[]>([]);
  products = signal<any[]>([]);

  // award form
  // Special achievement points are time-limited in v4.0 (ch.12), so the validity
  // window is part of the award, not an afterthought — an award without an end date
  // would silently be permanent.
  award = {
    category_key: '', rule_code: '', points: null as number | null, reason: '',
    valid_from: '', valid_to: ''
  };
  // membership form
  booking = { plan_code: '', paid_amount: null as number | null, currency: '', payment_date: '', no_referral: false, referrer_id: null as number | null, reason: '' };

  constructor() {
    this.loadMatrix();
    this.loadProducts();
    this.loadTopAccounts();
  }

  loadTopAccounts() {
    this.http.get<any>('/wpcp/admin/accounts').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (r) => this.topAccounts.set(r.accounts || []),
      error: (e) => this.logger.warn('accounts load failed', e)
    });
  }

  selectAccount(a: any) {
    this.selectUser({ id: a.user_id, full_name: a.full_name, email: a.email });
  }

  private loadMatrix() {
    this.http.get<any>('/wpcp/admin/matrix').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (m) => { this.categories.set(m.categories || []); this.rules.set(m.rules || []); },
      error: (e) => this.logger.warn('matrix load failed', e)
    });
  }
  private loadProducts() {
    this.http.get<any>('/wpcp/admin/membership_products').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (r) => this.products.set(r.products || []),
      error: (e) => this.logger.warn('products load failed', e)
    });
  }

  rulesForCategory(key: string) {
    return this.rules().filter((r) => r.category_key === key);
  }

  // Manual point awards only make sense for manual categories (contribution catalog);
  // automatic qualifications (Community Growth, Health Leadership tracks, First-Line
  // Builder) are computed by the engine and carry a fixed value, not a range.
  manualCategories() {
    return this.categories().filter((c) => c.mode === 'manual');
  }

  selectedAwardRule() {
    return this.rules().find((r) => r.code === this.award.rule_code) || null;
  }

  search() {
    const q = this.queryModel.trim();
    if (!q) { this.results.set([]); return; }
    this.searching.set(true);
    this.http.get<any>('/wpcp/admin/users/search', { params: new HttpParams().set('q', q) })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (r) => { this.results.set(r.users || []); this.searching.set(false); },
        error: (e) => { this.logger.error('search failed', e); this.searching.set(false); }
      });
  }

  selectUser(u: any) {
    this.selected.set(u);
    this.results.set([]);
    this.queryModel = '';
    this.reloadMember();
  }

  private reloadMember() {
    const u = this.selected();
    if (!u) return;
    this.loading.set(true);
    this.http.get<any>(`/wpcp/admin/users/${u.id}/summary`).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (s) => { this.summary.set(s); this.loading.set(false); },
      error: (e) => { this.logger.error('summary failed', e); this.loading.set(false); }
    });
    this.http.get<any>(`/wpcp/admin/users/${u.id}/history`).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (r) => this.history.set(r.history || []),
      error: (e) => this.logger.warn('history failed', e)
    });
    this.loadTopAccounts();
  }

  submitAward() {
    const u = this.selected();
    if (!u || this.award.points == null || !this.award.category_key) return;
    this.http.post<any>(`/wpcp/admin/users/${u.id}/award`, this.award)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => { this.flash('success', 'Points awarded'); this.award = { category_key: '', rule_code: '', points: null, reason: '', valid_from: '', valid_to: '' }; this.reloadMember(); },
        error: (e) => this.flash('danger', e?.error?.error || 'Award failed')
      });
  }

  recalcMember() {
    const u = this.selected();
    if (!u) return;
    this.http.post<any>(`/wpcp/admin/users/${u.id}/recalculate`, {})
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => { this.flash('success', 'Recalculated'); this.reloadMember(); },
        error: (e) => this.flash('danger', e?.error?.error || 'Failed')
      });
  }

  recalcAll() {
    this.http.post<any>('/wpcp/admin/recalculate_all', {})
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.flash('success', 'Recalculation started'),
        error: (e) => this.flash('danger', e?.error?.error || 'Failed')
      });
  }

  deactivate(tx: any) {
    const u = this.selected();
    if (!u) return;
    const state = tx.state === 'active' ? 'deactivated' : 'reactivated';
    this.http.post<any>(`/wpcp/admin/transactions/${tx.id}/state`, { state, reason: 'admin toggle' })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => { this.reloadMember(); },
        error: (e) => this.flash('danger', e?.error?.error || 'Failed')
      });
  }

  submitBooking() {
    const u = this.selected();
    if (!u || !this.booking.plan_code || !this.booking.reason) return;
    this.http.post<any>('/wpcp/admin/memberships', { ...this.booking, user_id: u.id })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => { this.flash('success', 'Membership booked'); this.booking = { plan_code: '', paid_amount: null, currency: '', payment_date: '', no_referral: false, referrer_id: null, reason: '' }; this.reloadMember(); },
        error: (e) => this.flash('danger', e?.error?.error || 'Booking failed')
      });
  }

  private flash(type: string, text: string) {
    this.message.set({ type, text });
    setTimeout(() => this.message.set(null), 4000);
  }
}
